import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { 
  getJobOffers, 
  getJobOfferById, 
  createJobOffer, 
  updateJobOffer, 
  previewJobEditImpact,
  archiveJobOffer, 
  updateJobStatus,
  analyzeDocuments,
  deleteJobOffer,
  getJobStatistics,
  getJobCandidates,
  getJobPipeline,
  createPipelineStage,
  updatePipelineStage,
  deletePipelineStage,
  selectCandidatesForInterview,
  completeJobInformation,
  uploadCvsForJob,
  getJobPlannedQuestions,
  toggleAuthorizeCvUpload,
  updateCvInterviewThreshold,
  toggleEnableCvAutoInterview,
  getCandidateQuestionsAnswers,
  uploadResumesToBank,
  searchResumeBank,
  linkResumesToJob,
  deleteCandidate,
  getCvSourceStats,
  getSourcingTags,
  createSourcingTag,
  deleteSourcingTag,
} from "../services/job.service";
import type { CvSourceStat } from "../services/job.service";
import { JobOffer, Candidate } from "../models/job";
import type { JobPipeline } from "../models/job";
import { applyOptimisticStageOrder } from "../utils/pipeline-cache";
import { writeQueryDataSync } from "../utils/query-sync-write";
import { 
  JobStatus,
  JobStatistics,
  PaginatedJobOffersResponse,
  JobOfferCreateData,
  JobOfferUpdateData,
  ExamConfig,
  JobPlannedQuestionsListResponse,
  CandidateQuestionsAnswersResponse,
  ResumeSearchRequest,
  ResumeSearchResponse,
  LinkResumeRequest,
  LinkResumeResponse,
  DocumentUploadResponse,
} from "../types/job.types";
import { useInvalidateUsageQueries } from "./useUsage";

// Query keys
export const jobKeys = {
  all: ['jobs'] as const,
  lists: () => [...jobKeys.all, 'list'] as const,
  list: (filters: { isActive?: boolean, status?: JobStatus }) => 
    [...jobKeys.lists(), { filters }] as const,
  paginatedList: (filters: { isActive?: boolean, status?: JobStatus, page: number, size: number, searchTerm?: string, searchScope?: string }) =>
    [...jobKeys.lists(), 'paginated', { filters }] as const,
  details: () => [...jobKeys.all, 'detail'] as const,
  detail: (id: string) => [...jobKeys.details(), id] as const,
  statistics: () => [...jobKeys.all, 'statistics'] as const,
  candidates: () => [...jobKeys.all, 'candidates'] as const,
  candidatesForJob: (jobId: string) => [...jobKeys.candidates(), jobId] as const,
  pipelineForJob: (jobId: string) => [...jobKeys.all, 'pipeline', jobId] as const,
  cvSourceStats: (jobId: string) => [...jobKeys.detail(jobId), 'cv-source-stats'] as const,
  sourcingTags: () => [...jobKeys.all, 'sourcing-tags'] as const,
  candidateQuestionsAnswers: (candidateId: string) => [...jobKeys.all, 'candidateQuestionsAnswers', candidateId] as const
};

// Reusable query client config
const DEFAULT_STALE_TIME = 5 * 60 * 1000; // 5 minutes
const DEFAULT_CACHE_TIME = 10 * 60 * 1000; // 10 minutes

// Map job status to appropriate refetch interval
export const getRefetchIntervalForStatus = (status?: string): number | false => {
  switch (status) {
    case JobStatus.ANALYZING:
      return 2000; // Poll every second for analyzing jobs
    case JobStatus.ACTIVE:
      return 10000; // Poll every 10 seconds for active jobs
    case JobStatus.INTERVIEWING:
      return 5000; // Poll every 5 seconds for active/interviewing jobs
    case JobStatus.DRAFT:
      return 2000; // Poll every 2 seconds for draft jobs
    case JobStatus.CLOSED:
    default:
      return false; // No polling for draft/closed jobs
  }
};

// Define type for job offers list response
type JobOffersListData = { job_offers: JobOffer[], total: number };
type PaginatedJobOffersListData = { items: JobOffer[], total: number, page: number, pages: number, size: number };

// New paginated hook for all active jobs - single API call
export const useAllJobOffers = (
  isActive?: boolean,
  page: number = 1,
  size: number = 20,
  status?: JobStatus,
  searchTerm?: string,
  options?: { enabled?: boolean, refetchInterval?: number | false },
  searchScope: 'user' | 'admin' = 'user'
) => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<PaginatedJobOffersResponse, Error>({
    queryKey: jobKeys.paginatedList({ isActive, status, page, size, searchTerm, searchScope }),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobOffers(accessToken, isActive, status, page, size, searchTerm, searchScope);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      if (!response.data) {
        throw new Error("No data returned from the server");
      }
      
      return response.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    placeholderData: keepPreviousData, // Keep showing previous page while loading new page
    refetchInterval: options?.refetchInterval ?? 10000, // Poll every 10 seconds by default
    ...options
  });
};

// Legacy hook - kept for backward compatibility with other parts of the app
export const useJobOffers = (isActive?: boolean, status?: JobStatus, options?: { enabled?: boolean, refetchInterval?: number | false }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<PaginatedJobOffersResponse, Error>({
    queryKey: jobKeys.list({ isActive, status }),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobOffers(accessToken, isActive, status);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      if (!response.data) {
        throw new Error("No data returned from the server");
      }
      
      return response.data;
    },
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    refetchInterval: options?.refetchInterval !== undefined 
      ? options.refetchInterval 
      : (status ? getRefetchIntervalForStatus(status) : 60000),
    ...options
  });
};

export const useJobOffer = (jobId: string, options?: { refetchInterval?: number | false; enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = options?.enabled !== false && !!jobId;

  return useQuery<JobOffer | undefined, Error>({
    queryKey: jobKeys.detail(jobId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobOfferById(accessToken, jobId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    refetchInterval: (queryData) => {
      // The data is accessible through the query object's data property
      const job = queryData.state.data as JobOffer | undefined;
      if (!job) return options?.refetchInterval ?? 50000;
      return options?.refetchInterval ?? getRefetchIntervalForStatus(job.status);
    },
  });
};

export const useCreateJobOffer = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (title: string) => {
      const accessToken = await getAccessTokenSilently();
      
      // Create a minimal job offer with just the title
      const jobData: JobOfferCreateData = {
        title,
        description: "",
        status: JobStatus.DRAFT,
        is_active: true
      };
      
      const response = await createJobOffer(accessToken, jobData);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onMutate: async () => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: jobKeys.lists() });
      
      // Get snapshot of the current data
      const previousJobs = queryClient.getQueryData<JobOffersListData>(
        jobKeys.list({ isActive: true, status: JobStatus.DRAFT })
      );
      
      // Return context with the snapshot
      return { previousJobs };
    },
    onSuccess: () => {
      // Invalidate job lists queries to refetch them
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
    },
    onError: (_, __, context) => {
      // If mutation fails, use the context we saved to roll back
      if (context?.previousJobs) {
        queryClient.setQueryData(
          jobKeys.list({ isActive: true, status: JobStatus.DRAFT }),
          context.previousJobs
        );
      }
    }
  });
};

export const useUpdateJobOffer = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobData: JobOfferUpdateData) => {
      const accessToken = await getAccessTokenSilently();
      const response = await updateJobOffer(accessToken, jobId, jobData);

      if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to update job offer');
      }

      return response.data;
    },
    onSuccess: ({ job }) => {
      // No optimistic merge: the payload is snake_case while the cached model is
      // camelCase, so write the server's mapped job instead.
      queryClient.setQueryData(jobKeys.detail(jobId), job);
      queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
      // Scoring-relevant edits flag candidates as being rescored.
      queryClient.invalidateQueries({ queryKey: jobKeys.candidatesForJob(jobId) });
    },
  });
};

export const useJobEditImpact = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useMutation({
    mutationFn: async (jobData: JobOfferUpdateData) => {
      const accessToken = await getAccessTokenSilently();
      const response = await previewJobEditImpact(accessToken, jobId, jobData);

      if (response.error || !response.data) {
        throw new Error(response.error || 'Failed to preview job edit');
      }

      return response.data;
    },
  });
};

export const useArchiveJobOffer = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const invalidateJobQueries = useInvalidateJobQueries();
  const invalidateUsageQueries = useInvalidateUsageQueries();
  
  return useMutation({
    mutationFn: async (jobId: string) => {
      const accessToken = await getAccessTokenSilently();
      const response = await archiveJobOffer(accessToken, jobId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onMutate: async (jobId) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: jobKeys.detail(jobId) });
      
      // Get current data for rollback
      const previousJob = queryClient.getQueryData<JobOffer>(jobKeys.detail(jobId));
      const activeJobsData = queryClient.getQueryData<PaginatedJobOffersListData>(jobKeys.list({ isActive: true }));
      
      // Optimistically update UI
      if (previousJob) {
        queryClient.setQueryData(jobKeys.detail(jobId), {
          ...previousJob,
          isActive: false,
          status: JobStatus.CLOSED
        });
      }
      
      // Optimistically remove from active jobs list
      if (activeJobsData) {
        queryClient.setQueryData(
          jobKeys.list({ isActive: true }),
          {
            ...activeJobsData,
            items: activeJobsData.items.filter(j => j.id !== jobId)
          }
        );
      }
      
      return { previousJob, activeJobsData };
    },
    onSuccess: (_, jobId) => {
      // Use our invalidation hook without polling
      invalidateJobQueries(jobId, { shouldPoll: false });
      
      // Invalidate usage queries since archiving affects usage counts
      invalidateUsageQueries();
    },
    onError: (_, jobId, context) => {
      // Rollback on error
      if (context?.previousJob) {
        queryClient.setQueryData(jobKeys.detail(jobId), context.previousJob);
      }
      if (context?.activeJobsData) {
        queryClient.setQueryData(
          jobKeys.list({ isActive: true }),
          context.activeJobsData
        );
      }
    }
  });
};

export const useUpdateJobStatus = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const invalidateJobQueries = useInvalidateJobQueries();
  const invalidateUsageQueries = useInvalidateUsageQueries();
  
  return useMutation({
    mutationFn: async (status: JobStatus) => {
      const accessToken = await getAccessTokenSilently();
      const response = await updateJobStatus(accessToken, jobId, status);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onMutate: async (status) => {
      await queryClient.cancelQueries({ queryKey: jobKeys.detail(jobId) });
      
      const previousJob = queryClient.getQueryData<JobOffer>(jobKeys.detail(jobId));
      
      if (previousJob) {
        queryClient.setQueryData(jobKeys.detail(jobId), {
          ...previousJob,
          status
        });
      }
      
      return { previousJob };
    },
    onSuccess: () => {
      // Use our invalidation hook with polling
      invalidateJobQueries(jobId, { shouldPoll: true });
      
      // Invalidate usage queries since job status changes affect usage counts
      invalidateUsageQueries();
    },
    onError: (_, __, context) => {
      if (context?.previousJob) {
        queryClient.setQueryData(jobKeys.detail(jobId), context.previousJob);
      }
    }
  });
};

export const useAnalyzeDocuments = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const invalidateJobQueries = useInvalidateJobQueries();
  const invalidateUsageQueries = useInvalidateUsageQueries();
  
  return useMutation({
    mutationFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await analyzeDocuments(accessToken, jobId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onMutate: async () => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: jobKeys.detail(jobId) });
      await queryClient.cancelQueries({ queryKey: jobKeys.lists() });
      
      // Get snapshots for rollback
      const previousJob = queryClient.getQueryData<JobOffer>(jobKeys.detail(jobId));
      
      return { previousJob };
    },
    onSuccess: () => {
      // Use our invalidation hook with polling
      invalidateJobQueries(jobId, { shouldPoll: true });
      
      // Invalidate usage queries since document analysis can lead to job status changes
      invalidateUsageQueries();
    },
    onError: (_, __, context) => {
      // Rollback on error
      if (context?.previousJob) {
        queryClient.setQueryData(jobKeys.detail(jobId), context.previousJob);
      }
    }
  });
};

// Hook for deleting a job offer
export const useDeleteJobOffer = () => {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateJobQueries = useInvalidateJobQueries();
  const invalidateUsageQueries = useInvalidateUsageQueries();
  
  return useMutation({
    mutationFn: async (jobId: string) => {
      const token = await getAccessTokenSilently();
      const response = await deleteJobOffer(token, jobId);
      
      if (response.error) {
        throw new Error('Failed to delete job offer');
      }
      
      return jobId;
    },
    onSuccess: (jobId) => {
      // We don't need polling for deletion, but we want to refresh all listings
      invalidateJobQueries(jobId, { shouldPoll: false });
      
      // Invalidate usage queries since deleting affects usage counts
      invalidateUsageQueries();
    }
  });
};

// Hook for fetching job statistics
export const useJobStatistics = () => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<JobStatistics, Error>({
    queryKey: jobKeys.statistics(),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobStatistics(accessToken);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      if (!response.data) {
        throw new Error("No statistics data returned from the server");
      }
      
      return response.data;
    },
    staleTime: 30000, // 30 seconds
    refetchInterval: 60000, // 1 minute
  });
};

const IN_PROGRESS_SCORING_STATUSES = new Set(['stale', 'rescoring']);

/** True while a job-edit rescore is still running for any candidate. */
const hasInProgressRescore = (candidates: Candidate[] | undefined): boolean =>
  Boolean(candidates?.some((c) => c.scoringStatus && IN_PROGRESS_SCORING_STATUSES.has(c.scoringStatus)));

// Hook for getting job candidates
export const useJobCandidates = (jobId: string, options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = (options?.enabled !== false) && !!jobId;

  return useQuery<Candidate[], Error>({
    queryKey: jobKeys.candidatesForJob(jobId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobCandidates(accessToken, jobId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data || [];
    },
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    // Job-edit rescore is async; failures never emit SSE. Poll while any chip is
    // stale/rescoring so the UI settles to failed / clear without a hard reload.
    refetchInterval: (query) => (hasInProgressRescore(query.state.data) ? 2000 : false),
  });
};

/** Job-scoped pipeline stages (Kanban columns). */
export const useJobPipeline = (jobId: string, options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = (options?.enabled !== false) && !!jobId;

  return useQuery({
    queryKey: jobKeys.pipelineForJob(jobId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobPipeline(accessToken, jobId);
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data;
    },
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
  });
};

/** Mutations that keep job pipeline stages in sync with the backend (SoT). */
export const useJobPipelineMutations = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const invalidatePipeline = () => {
    queryClient.invalidateQueries({
      queryKey: jobKeys.pipelineForJob(jobId),
      refetchType: 'all',
    });
    queryClient.invalidateQueries({
      queryKey: jobKeys.candidatesForJob(jobId),
      refetchType: 'all',
    });
  };

  const createStage = useMutation({
    mutationFn: async (input: {
      label: string;
      positionAfterStageId?: string | null;
    }) => {
      const token = await getAccessTokenSilently();
      const response = await createPipelineStage(token, jobId, input);
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    onSuccess: invalidatePipeline,
  });

  const updateStage = useMutation({
    mutationFn: async (input: {
      stageId: string;
      label?: string;
      position?: number;
      automationEnabled?: boolean;
    }) => {
      const token = await getAccessTokenSilently();
      const response = await updatePipelineStage(token, jobId, input.stageId, {
        label: input.label,
        position: input.position,
        automationEnabled: input.automationEnabled,
      });
      if (response.error) throw new Error(response.error);
      return response.data;
    },
    // Synchronous so a column drop commits in the same React commit that ends
    // the drag — see writeQueryDataSync.
    onMutate: (input) => {
      const hasPosition = input.position !== undefined;
      const hasAutomation = input.automationEnabled !== undefined;
      if (!hasPosition && !hasAutomation) return undefined;

      const queryKey = jobKeys.pipelineForJob(jobId);
      // `revert: false`: see useCandidatePipelineDnd — a reverting cancel would
      // undo the write below.
      void queryClient.cancelQueries({ queryKey }, { revert: false });
      const previousPipeline = queryClient.getQueryData<JobPipeline>(queryKey);

      writeQueryDataSync(() => {
        if (hasPosition) {
          queryClient.setQueryData<JobPipeline>(queryKey, (current) =>
            applyOptimisticStageOrder(current, {
              stageId: input.stageId,
              position: input.position as number,
            })
          );
        }
        if (hasAutomation) {
          queryClient.setQueryData<JobPipeline>(queryKey, (current) => {
            if (!current) return current;
            return {
              ...current,
              stages: current.stages.map((stage) =>
                stage.id === input.stageId
                  ? {
                      ...stage,
                      automationEnabled: input.automationEnabled as boolean,
                    }
                  : stage
              ),
            };
          });
        }
      });

      return { previousPipeline };
    },
    onError: (_error, _input, context) => {
      if (!context?.previousPipeline) return;
      queryClient.setQueryData(
        jobKeys.pipelineForJob(jobId),
        context.previousPipeline
      );
    },
    onSuccess: invalidatePipeline,
  });

  const deleteStage = useMutation({
    mutationFn: async (stageId: string) => {
      const token = await getAccessTokenSilently();
      const response = await deletePipelineStage(token, jobId, stageId);
      if (response.error) throw new Error(response.error);
      return true;
    },
    onSuccess: invalidatePipeline,
  });

  return { createStage, updateStage, deleteStage };
};

// Hook for selecting candidates for interview
export const useSelectCandidatesForInterview = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateJobQueries = useInvalidateJobQueries();
  
  return useMutation({
    mutationFn: async ({
      candidateIds,
      examConfig,
      sendNotification = true,
    }: {
      candidateIds: string[];
      examConfig: ExamConfig;
      sendNotification?: boolean;
    }) => {
      const token = await getAccessTokenSilently();
      const response = await selectCandidatesForInterview(
        token,
        jobId,
        candidateIds,
        examConfig,
        sendNotification
      );
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onSuccess: () => {
      // Use our custom hook to handle invalidation with aggressive polling
      invalidateJobQueries(jobId, { shouldPoll: true });
    }
  });
};

// Hook for adding CVs to an active job (supports single or multiple files)
export const useAddCvToJob = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const invalidateJobQueries = useInvalidateJobQueries();
  
  return useMutation({
    mutationFn: async (files: File[]) => {
      const token = await getAccessTokenSilently();
      const BATCH_SIZE = 30;
      const allResults: DocumentUploadResponse[] = [];
      
      for (let i = 0; i < files.length; i += BATCH_SIZE) {
        const batch = files.slice(i, i + BATCH_SIZE);
        const batchNumber = Math.floor(i / BATCH_SIZE) + 1;
        const totalBatches = Math.ceil(files.length / BATCH_SIZE);
        
        console.log(`Uploading batch ${batchNumber}/${totalBatches} (${batch.length} files)`);
        
        const response = await uploadCvsForJob(token, jobId, batch);
        
        if (response.error) {
          throw new Error(response.error || `Failed to upload batch ${batchNumber}/${totalBatches}`);
        }
        
        if (response.data) {
          allResults.push(...response.data);
        }
      }
      
      return allResults;
    },
    onSuccess: () => {
      // Invalidate and refresh job data to show the new documents
      // SSE events handle real-time updates, so no need for aggressive polling
      invalidateJobQueries(jobId, { shouldPoll: false });
    }
  });
};

// Hook to get a function that can invalidate job queries
export const useInvalidateJobQueries = () => {
  const queryClient = useQueryClient();
  
  return (jobId: string, options: { shouldPoll?: boolean } = {}) => {
    const { shouldPoll = true } = options;
    
    // Aggressively invalidate the candidates list, job details, and exam preview
    queryClient.invalidateQueries({ queryKey: jobKeys.candidatesForJob(jobId), refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: jobKeys.pipelineForJob(jobId), refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId), refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: jobKeys.lists(), refetchType: 'all' });
    queryClient.invalidateQueries({ queryKey: ['exam-preview', jobId] });
    
    // Force immediate refetch
    queryClient.refetchQueries({ queryKey: jobKeys.candidatesForJob(jobId), type: 'active' });
    queryClient.refetchQueries({ queryKey: jobKeys.pipelineForJob(jobId), type: 'active' });
    queryClient.refetchQueries({ queryKey: jobKeys.detail(jobId), type: 'active' });
    queryClient.refetchQueries({ queryKey: jobKeys.lists(), type: 'active' });
    
    // Set up aggressive polling for the next 10 seconds if requested
    if (shouldPoll) {
      const refetchInterval = 1000; // 1 second
      const maxRefetches = 10;
      let refetchCount = 0;
      
      const intervalId = setInterval(() => {
        queryClient.refetchQueries({ queryKey: jobKeys.candidatesForJob(jobId) });
        queryClient.refetchQueries({ queryKey: jobKeys.detail(jobId) });
        queryClient.refetchQueries({ queryKey: jobKeys.lists() });
        
        refetchCount++;
        if (refetchCount >= maxRefetches) {
          clearInterval(intervalId);
        }
      }, refetchInterval);
    }
  };
};

export const useCompleteJobInformation = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: Record<string, string>) => {
      const token = await getAccessTokenSilently();
      return completeJobInformation(token, jobId, formData);
    },
    onSuccess: (response) => {
      // Invalidate this specific job
      queryClient.invalidateQueries({ 
        queryKey: [jobKeys.detail(jobId)] 
      });
      
      // Also invalidate job lists since status might have changed
      queryClient.invalidateQueries({ 
        queryKey: [jobKeys.lists()] 
      });
      
      return response;
    }
  });
};

// Hook for getting planned questions for a job
export const useJobPlannedQuestions = (jobId: string, options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<JobPlannedQuestionsListResponse, Error>({
    queryKey: ['job-planned-questions', jobId],
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getJobPlannedQuestions(accessToken, jobId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      if (!response.data) {
        throw new Error("No planned questions data returned from the server");
      }
      
      return response.data;
    },
    enabled: (options?.enabled ?? true) && !!jobId,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
  });
};

// Hook for toggling authorize CV upload
export const useToggleAuthorizeCvUpload = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (authorize: boolean) => {
      const accessToken = await getAccessTokenSilently();
      const response = await toggleAuthorizeCvUpload(accessToken, jobId, authorize);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the job details to get the updated state
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
    }
  });
};

// Hook for fetching per-channel public CV upload counts
export const useCvSourceStats = (jobId: string, options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = (options?.enabled !== false) && !!jobId;

  return useQuery<CvSourceStat[], Error>({
    queryKey: jobKeys.cvSourceStats(jobId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getCvSourceStats(accessToken, jobId);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data?.sources ?? [];
    },
    enabled,
    staleTime: 30000,
  });
};

export const useSourcingTags = (options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  const enabled = options?.enabled !== false;

  return useQuery<string[], Error>({
    queryKey: jobKeys.sourcingTags(),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getSourcingTags(accessToken);

      if (response.error) {
        throw new Error(response.error);
      }

      return response.data?.items?.map((tag) => tag.slug) ?? [];
    },
    enabled,
    staleTime: 30000,
  });
};

export const useCreateSourcingTag = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      const accessToken = await getAccessTokenSilently();
      const response = await createSourcingTag(accessToken, name);

      if (response.error) {
        throw new Error(response.error);
      }

      if (!response.data?.slug) {
        throw new Error('Failed to create sourcing tag');
      }

      return response.data.slug;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.sourcingTags() });
    },
  });
};

export const useDeleteSourcingTag = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (slug: string) => {
      const accessToken = await getAccessTokenSilently();
      const response = await deleteSourcingTag(accessToken, slug);

      if (response.error) {
        throw new Error(response.error);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.sourcingTags() });
    },
  });
};

// Hook for updating CV interview threshold
export const useUpdateCvInterviewThreshold = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (threshold: number) => {
      const accessToken = await getAccessTokenSilently();
      const response = await updateCvInterviewThreshold(accessToken, jobId, threshold);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the job details to get the updated state
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
    }
  });
};

// Hook for toggling enable CV auto-interview
export const useToggleEnableCvAutoInterview = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (enable: boolean) => {
      const accessToken = await getAccessTokenSilently();
      const response = await toggleEnableCvAutoInterview(accessToken, jobId, enable);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch the job details to get the updated state
      queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });
    }
  });
};

// Hook for fetching candidate questions and answers
export const useCandidateQuestionsAnswers = (candidateId: string, options?: { enabled?: boolean }) => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<CandidateQuestionsAnswersResponse | undefined, Error>({
    queryKey: jobKeys.candidateQuestionsAnswers(candidateId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await getCandidateQuestionsAnswers(accessToken, candidateId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    enabled: !!(candidateId && (options?.enabled !== false)),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_CACHE_TIME,
    // Questions and answers don't change often, so we can use a longer refetch interval
    refetchInterval: false
  });
};

// Hook for uploading resumes to the resume bank
export const useUploadResumesToBank = () => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useMutation({
    mutationFn: async (files: File[]) => {
      const token = await getAccessTokenSilently();
      const response = await uploadResumesToBank(token, files);
      
      if (response.error) {
        throw new Error(response.error || 'Failed to upload resumes to bank');
      }
      
      return response.data || [];
    },
    // Don't invalidate here - wait for batch complete notification from backend
    // App.tsx will handle the invalidation when the chord callback fires
  });
};

export const useSearchResumeBank = (request: ResumeSearchRequest) => {
  const { getAccessTokenSilently } = useAuth0();
  
  return useQuery<ResumeSearchResponse>({
    queryKey: ['resumeBank', 'search', request],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      const response = await searchResumeBank(token, request);
      
      if (response.error) {
        throw new Error(response.error || 'Failed to search resume bank');
      }
      
      return response.data as ResumeSearchResponse;
    },
    enabled: !!request.filters
  });
};

export const useLinkResumesToJob = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const invalidateUsageQueries = useInvalidateUsageQueries();
  
  return useMutation({
    mutationFn: async (request: LinkResumeRequest) => {
      const token = await getAccessTokenSilently();
      const response = await linkResumesToJob(token, request);
      
      if (response.error) {
        throw new Error(response.error || 'Failed to link resumes to job');
      }
      
      return response.data as LinkResumeResponse;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ 
        queryKey: jobKeys.detail(variables.job_offer_id) 
      });
      queryClient.invalidateQueries({ 
        queryKey: jobKeys.candidatesForJob(variables.job_offer_id) 
      });
      invalidateUsageQueries();
    }
  });
};

// Hook for deleting a candidate
export const useDeleteCandidate = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (candidateId: string) => {
      const accessToken = await getAccessTokenSilently();
      const response = await deleteCandidate(accessToken, candidateId);
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return candidateId;
    },
    onMutate: async (candidateId) => {
      await queryClient.cancelQueries({ queryKey: jobKeys.candidatesForJob(jobId) });
      
      const previousCandidates = queryClient.getQueryData<Candidate[]>(
        jobKeys.candidatesForJob(jobId)
      );
      
      if (previousCandidates) {
        const updatedCandidates = previousCandidates.filter(c => c.id !== candidateId);
        queryClient.setQueryData(jobKeys.candidatesForJob(jobId), updatedCandidates);
      }
      
      return { previousCandidates };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobKeys.candidatesForJob(jobId) });
    },
    onError: (_, __, context) => {
      if (context?.previousCandidates) {
        queryClient.setQueryData(jobKeys.candidatesForJob(jobId), context.previousCandidates);
      }
    }
  });
};
